import {
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
} from '@cloudscape-design/components';
import { LotInspectionResults } from '@parallel-fluidics/shared/constants';
import PropTypes from 'prop-types';
import React from 'react';

/** Short display label for a data link: last path segment, else hostname. */
function linkLabel(url) {
  try {
    const { hostname, pathname } = new URL(url);
    const lastSegment = pathname.split('/').filter(Boolean).pop();
    return lastSegment || hostname;
  } catch {
    return url;
  }
}

/**
 * Inspection history for a lot, newest first, one bordered row per
 * record. Every re-inspection is its own record — there is no single
 * "current" inspection field on the lot.
 */
export default function LotInspectionsCard({ inspections, onRecord }) {
  return (
    <Container
      header={(
        <Header
          variant="h2"
          actions={<Button onClick={onRecord}>Record inspection</Button>}
        >
          Inspections
        </Header>
      )}
    >
      {inspections.length === 0 ? (
        <Box color="text-status-inactive">Not inspected yet</Box>
      ) : (
        <ColumnLayout columns={1} borders="horizontal">
          {inspections.map((inspection) => (
            <div key={inspection.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Badge color={inspection.result === LotInspectionResults.PASSED ? 'green' : 'red'}>
                    {inspection.result || 'unknown'}
                  </Badge>
                  <Box color="text-body-secondary">
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    {inspection._createdBy?.timestamp?.toDate?.().toLocaleString() || '—'}
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    {inspection._createdBy?.email ? ` · ${inspection._createdBy.email}` : ''}
                  </Box>
                </div>
                {inspection.dataLinks?.length > 0 && (
                  <SpaceBetween direction="horizontal" size="xs">
                    {inspection.dataLinks.map((dataLink, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Link key={`${index}-${dataLink}`} external href={dataLink}>
                        {linkLabel(dataLink)}
                      </Link>
                    ))}
                  </SpaceBetween>
                )}
              </div>
              <Box margin={{ top: 'xs' }}>
                <span style={{ whiteSpace: 'pre-line' }}>{inspection.note}</span>
              </Box>
            </div>
          ))}
        </ColumnLayout>
      )}
    </Container>
  );
}

LotInspectionsCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  inspections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRecord: PropTypes.func.isRequired,
};
